import { BaseMigration } from '@Lib/Migrations/Base';
import { compareSemVersions } from '@Lib/Version';
import { lastElement } from '@standardnotes/utils';
import { RawStorageKey, namespacedKey, ApplicationEvent, ApplicationStage, AbstractService, } from '@standardnotes/services';
import { SnjsVersion, isRightVersionGreaterThanLeft } from '../../Version';
import { SNLog } from '@Lib/Log';
import { MigrationClasses } from '@Lib/Migrations/Versions';
/**
 * The migration service orchestrates the execution of multi-stage migrations.
 * Migrations are registered during initial application launch, and listen for application
 * life-cycle events, and act accordingly. Migrations operate on the app-level, and not global level.
 * For example, a single migration may perform a unique set of steps when the application
 * first launches, and also other steps after the application is unlocked, or after the
 * first sync completes. Migrations live under /migrations and inherit from the base Migration class.
 */
export class MigrationService extends AbstractService {
    constructor(services) {
        super(services.internalEventBus);
        this.services = services;
    }
    deinit() {
        ;
        this.services = undefined;
        if (this.activeMigrations) {
            this.activeMigrations.length = 0;
        }
        super.deinit();
    }
    async initialize() {
        await this.runBaseMigrationPreRun();
        const requiredMigrations = MigrationService.getRequiredMigrations(await this.getStoredSnjsVersion());
        this.activeMigrations = this.instantiateMigrationClasses(requiredMigrations);
        if (this.activeMigrations.length > 0) {
            const lastMigration = lastElement(this.activeMigrations);
            lastMigration.onDone(async () => {
                await this.markMigrationsAsDone();
            });
        }
        else {
            await this.markMigrationsAsDone();
        }
    }
    async markMigrationsAsDone() {
        await this.services.deviceInterface.setRawStorageValue(namespacedKey(this.services.identifier, RawStorageKey.SnjsVersion), SnjsVersion);
    }
    async runBaseMigrationPreRun() {
        this.baseMigration = new BaseMigration(this.services);
        await this.baseMigration.preRun();
    }
    async handleEvent(event) {
        if (event.type === ApplicationEvent.ApplicationStageChanged) {
            const stage = event.payload.stage;
            await this.handleStage(stage);
        }
    }
    /**
     * Called by application
     */
    async handleApplicationEvent(event) {
        if (event === ApplicationEvent.SignedIn) {
            await this.handleStage(ApplicationStage.SignedIn_30);
        }
    }
    async hasPendingMigrations() {
        const requiredMigrations = MigrationService.getRequiredMigrations(await this.getStoredSnjsVersion());
        return requiredMigrations.length > 0 || (await this.baseMigration.needsKeychainRepair());
    }
    async getStoredSnjsVersion() {
        const version = await this.services.deviceInterface.getRawStorageValue(namespacedKey(this.services.identifier, RawStorageKey.SnjsVersion));
        if (!version) {
            throw SNLog.error(Error('Snjs version missing from storage, run base migration.'));
        }
        return version;
    }
    static getRequiredMigrations(storedVersion) {
        const resultingClasses = [];
        const sortedClasses = MigrationClasses.sort((a, b) => {
            return compareSemVersions(a.version(), b.version());
        });
        for (const migrationClass of sortedClasses) {
            const migrationVersion = migrationClass.version();
            if (migrationVersion === storedVersion) {
                continue;
            }
            if (isRightVersionGreaterThanLeft(storedVersion, migrationVersion)) {
                resultingClasses.push(migrationClass);
            }
        }
        return resultingClasses;
    }
    instantiateMigrationClasses(classes) {
        return classes.map((migrationClass) => {
            return new migrationClass(this.services);
        });
    }
    async handleStage(stage) {
        await this.baseMigration.handleStage(stage);
        if (!this.activeMigrations) {
            throw new Error('Invalid active migrations');
        }
        for (const migration of this.activeMigrations) {
            await migration.handleStage(stage);
        }
    }
    getDiagnostics() {
        return Promise.resolve({
            migrations: {
                activeMigrations: this.activeMigrations && this.activeMigrations.map((m) => typeof m),
            },
        });
    }
}
